import {
  createContext,
  useContext,
  createSignal,
  createEffect,
  untrack,
} from "solid-js";

import { forgetThisDevice } from "@uic/domain/push";
import { setTokenFn } from "@uic/domain/wsc";

import {
  passwordSignin,
  passwordSignup,
  signout,
  getUser,
  refreshToken,
} from "../core/authn";

/**
 * @typedef {import('../types').User} User
 */

/**
 * @typedef {object} AuthnContextValue
 * @property {() => User} user - The current authenticated user
 * @property {() => boolean} isAuthenticated - Whether a user is logged in
 * @property {() => boolean} isLoading - Whether auth state is being initialized
 * @property {(email: string, password: string) => Promise<User>} passwordSignin - Login with email/password
 * @property {(email: string, name: string, password: string) => Promise<User>} passwordSignup - Create a new password account
 * @property {() => Promise<void>} signout - Logout the current user
 * @property {() => void} refreshUser - Re-read the user from the auth store
 */

const AuthnContext = createContext();

/**
 * Hook to access the auth context
 * @returns {AuthnContextValue} Object to help with authn actions
 */
export function useAuthn() {
  const context = useContext(AuthnContext);
  if (!context) {
    throw new Error("useAuthn must be used within an AuthnProvider");
  }
  return context;
}

/**
 * Authn provider component
 * @param {object} props Component props
 * @param {import('solid-js').JSX.Element} props.children The children
 * @returns {import('solid-js').JSX.Element} The provider element
 */
export function AuthnProvider(props) {
  const [user, setUser] = createSignal(/** @type {User | null} */ (null));
  const [isLoading, setIsLoading] = createSignal(true);

  setTokenFn(() => untrack(() => user()?.token));

  const initAuth = async () => {
    try {
      setIsLoading(true);

      let u = getUser();
      u = await refreshToken();
      setUser(u);
    } catch (error) {
      console.warn("Failed to initialize auth:", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Login with email and password
   * @param {string} email User's email
   * @param {string} password User's password
   * @returns {Promise<User>} The signed in user
   */
  const iPasswordSignin = async (email, password) => {
    const u = await passwordSignin(email, password);

    setUser(u);
    return u;
  };

  /**
   * Sign up a new user
   * @param {string} email User's email
   * @param {string} name User's name
   * @param {string} password User's password
   * @returns {Promise<User>} the newly created user
   */
  const iPasswordSignup = async (email, name, password) => {
    const _uId = await passwordSignup(email, name, password);
    const u = await passwordSignin(email, password);
    setUser(u);
    return u;
  };

  /**
   * Logout the current user
   */
  const iSignout = async () => {
    await forgetThisDevice();
    signout();
    setUser(null);
  };

  /**
   * Re-read the user from the auth store, so a profile edit made through the
   * PocketBase SDK reaches every consumer of the signal.
   */
  const refreshUser = () => {
    setUser(getUser());
  };

  /**
   * Check if user is authenticated
   * @returns {boolean} Yes or no
   */
  const isAuthenticated = () => user() !== null;

  createEffect(() => {
    initAuth();
  });

  const value = {
    user,
    isAuthenticated,
    isLoading,
    passwordSignin: iPasswordSignin,
    passwordSignup: iPasswordSignup,
    signout: iSignout,
    refreshUser,
  };

  return (
    <AuthnContext.Provider value={value}>
      {props.children}
    </AuthnContext.Provider>
  );
}
